/**
 * ScLearntCard (026, US3) — displays org research items surfaced during the
 * scoping session's orient/frame sections. Supersedes ScopingLearntCard from
 * spec 017. Presentational only — no actions.
 */

import { Sparkles } from 'lucide-react';

import { resolveSnapshotActionIcon } from '@/components/v2/shared/snapshotActionIcons';
import { cn } from '@/lib/utils';
import { ChatCard } from '../../widgets/chat-widget-ui';
import { capitalizeFirst } from '../../widgets/textFormat';
import { SkBar } from '../../widgets/skeletons';
import type { ScLearntCardProps } from '@shared/widget-schemas';

interface ScLearntCardComponentProps extends ScLearntCardProps {
  className?: string;
}

export function ScLearntCard({ items, className }: ScLearntCardComponentProps) {
  return (
    <ChatCard className={cn('flex flex-col gap-2 p-4', className)}>
      <div className="flex items-center gap-1.5">
        <Sparkles className="size-3 text-[#C084FC]" aria-hidden="true" />
        <span className="text-muted-foreground font-mono text-[10.5px] font-bold uppercase tracking-wide">
          What we know
        </span>
      </div>

      {/* 2026-07-25 — replaces the old per-item "Exploring" tags + footer disclaimer
          with one short intro line, instead of badging individual findings differently. */}
      <p className="text-muted-foreground text-[12px] leading-snug">
        This is what I've learnt from what you've shared and research.
      </p>

      <ul className="flex flex-col gap-2.5">
        {items.map((item, i) => {
          const Icon = resolveSnapshotActionIcon(item.icon) ?? Sparkles;
          // 2026-07-25 D5 — `statements[]` is the preferred (grouped) shape; a
          // pre-grouping producer's flat `{text}` still renders as a single-statement
          // pillar (back-compat).
          const statements = item.statements ?? (item.text ? [{ text: item.text }] : []);
          return (
            <li key={i} className="border-border flex items-start gap-3 border-t pt-2">
              <span
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg text-[#C084FC]"
                style={{ background: 'var(--chat-chip-bg)', border: '1px solid var(--chat-chip-border)' }}
              >
                <Icon className="size-3.5" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-foreground text-[13px] font-bold">{capitalizeFirst(item.pillar)}</span>
                  {item.source && (
                    <span
                      className="text-muted-foreground rounded-full border px-1.5 py-0.5 font-mono text-[9.5px] font-bold uppercase"
                      style={{ background: 'var(--secondary)', borderColor: 'var(--border)' }}
                    >
                      {item.source}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-col gap-1">
                  {statements.map((statement, j) => (
                    <p key={j} className="text-foreground-subtle text-[12.5px] leading-snug">
                      {capitalizeFirst(statement.text)}
                    </p>
                  ))}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </ChatCard>
  );
}

export function ScLearntCardSkeleton({ className }: { className?: string }) {
  return (
    <ChatCard className={cn('flex flex-col gap-2.5 p-4', className)}>
      <SkBar width="30%" height="10px" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex flex-col gap-1">
          <SkBar width="25%" height="9px" />
          <SkBar width="90%" height="13px" />
        </div>
      ))}
    </ChatCard>
  );
}
